import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import logo from "./assets/logo.svg";
import hanu_home from "./assets/hanu_home.png";
import "./home_page.css";
import { API_UPLOAD, API_VERIFY_MEMBER, post_json } from "./api";
import PortfolioDashboard from "./portfolio_dashboard";
import PortfolioPremiumLoader from "./portfolio_premium_loader";

const SESSION_KEY = "siho_portfolio_upload_data";

function download_sample_excel() {
  const wb = XLSX.utils.book_new();
  const data = [
    ["nse_code", "bse_code", "buy_price", "qty", "buy_date", "current_price"],
    ["RELIANCE", "", 2400, 10, "2023-01-15", 2950],
    ["INFY", "", 1450, 25, "2022-06-10", 1780],
    ["", "500209", 1600, 15, "2023-03-20", 1720],
    ["TCS", "", 3200, 5, "2021-11-05", 3850],
    ["", "532454", 1800, 20, "2022-09-01", 1950],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(data);
  ws1["!cols"] = [{ wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, ws1, "Portfolio (Upload This)");

  const info = [
    ["SIHO Research — Portfolio Upload Format"],
    [""],
    ["Column", "Required?", "Accepted header names", "Notes"],
    [
      "nse_code",
      "See note",
      "nse_code, nse code, nsecode, nse",
      "NSE ticker symbol. Leave blank if using bse_code.",
    ],
    [
      "bse_code",
      "See note",
      "bse_code, bse code, bsecode, bse",
      "BSE numeric code. Leave blank if using nse_code.",
    ],
    [
      "buy_price",
      "Required",
      "buy_price, buy price, buyprice, price",
      "Non-negative number.",
    ],
    ["qty", "Required", "qty, quantity", "Positive integer only."],
    ["buy_date", "Optional", "buy_date, buy date", "YYYY-MM-DD or DD-MM-YYYY."],
    [
      "current_price",
      "Optional",
      "current_price, current price",
      "If omitted, live price is fetched.",
    ],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(info);
  ws2["!cols"] = [{ wch: 16 }, { wch: 12 }, { wch: 52 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Instructions");
  XLSX.writeFile(wb, "siho_portfolio_template.xlsx");
}

export default function HomePage() {
  const file_input_ref = useRef(null);
  const verify_timer_ref = useRef(null);

  const [selected_file, set_selected_file] = useState(null);
  const [drag_over, set_drag_over] = useState(false);
  const [loading, set_loading] = useState(false);
  const [loader_complete, set_loader_complete] = useState(false);
  const [upload_error, set_upload_error] = useState("");

  const [internal_checked, set_internal_checked] = useState(false);
  const [internal_password, set_internal_password] = useState("");
  const [is_internal_member, set_is_internal_member] = useState(false);
  const [pw_status, set_pw_status] = useState({ type: "", text: "" });
  const [analyzing, set_analyzing] = useState(false);
  const [portfolio_data, set_portfolio_data] = useState(null);

  const clear_error = () => set_upload_error("");

  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && !parsed.error) set_portfolio_data(parsed);
    } catch {
      /* ignore */
    }
  }, []);

  const on_file_selected = useCallback((file) => {
    if (!file) return;
    set_selected_file(file);
    clear_error();
  }, []);

  const on_file_input_change = (e) => {
    const file = e.target.files?.[0];
    on_file_selected(file);
  };

  const on_drop = (e) => {
    e.preventDefault();
    set_drag_over(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) on_file_selected(file);
  };

  const on_internal_chk_change = (checked) => {
    set_internal_checked(checked);
    set_internal_password("");
    set_is_internal_member(false);
    set_pw_status({ type: "", text: "" });
  };

  const on_internal_pw_input = (value) => {
    set_internal_password(value);
    set_is_internal_member(false);

    if (verify_timer_ref.current) {
      clearTimeout(verify_timer_ref.current);
    }

    if (!value.length) {
      set_pw_status({ type: "", text: "" });
      return;
    }

    set_pw_status({ type: "", text: "Verifying…" });
    verify_timer_ref.current = setTimeout(() => {
      post_json(API_VERIFY_MEMBER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: value }),
      })
        .then(({ data: d }) => {
          if (d.valid) {
            set_is_internal_member(true);
            set_pw_status({ type: "ok", text: "Verified — internal access granted" });
          } else {
            set_is_internal_member(false);
            set_pw_status({ type: "err", text: "Incorrect password" });
          }
        })
        .catch(() => {
          set_is_internal_member(false);
          set_pw_status({ type: "err", text: "Verification failed — try again" });
        });
    }, 400);
  };

  const run_analysis = async () => {
    clear_error();

    if (internal_checked && !is_internal_member) {
      set_pw_status({
        type: "err",
        text: internal_password.length
          ? "Incorrect password — cannot proceed"
          : "Enter the member password to continue",
      });
      return;
    }

    if (!selected_file) {
      set_upload_error("Please upload your portfolio Excel or CSV file.");
      return;
    }

    set_analyzing(true);
    set_loading(true);

    const fd = new FormData();
    fd.append("file", selected_file);

    post_json(API_UPLOAD, { method: "POST", body: fd })
      .then(async ({ data: d }) => {
        if (d.error) {
          set_upload_error(d.error);
          return;
        }
        set_loader_complete(true);
        await new Promise((resolve) => window.setTimeout(resolve, 800));
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(d));
        set_portfolio_data(d);
      })
      .catch((e) => {
        set_upload_error(e.message || "Request failed");
      })
      .finally(() => {
        set_analyzing(false);
        set_loading(false);
        set_loader_complete(false);
      });
  };

  const zone_class = [
    "upload_zone",
    drag_over ? "drag_over" : "",
    selected_file ? "has_file" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const on_new_analysis = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    set_portfolio_data(null);
    set_selected_file(null);
    clear_error();
  }, []);

  if (portfolio_data) {
    return (
      <PortfolioDashboard
        data={portfolio_data}
        on_new_analysis={on_new_analysis}
        on_logout={on_new_analysis}
      />
    );
  }

  return (
    <div className="home_page app_page">
      <header className="home_page__header">
        <img src={logo} alt="SIHO Research" className="home_page__logo" />
      </header>

      <div className="home_page__body">
        <div className="home_page__title_block">
          <h1 className="home_page__heading">
            <span className="home_page__heading_theme">PortFolio</span> analyzer
          </h1>
          <p className="home_page__subheading">
            Upload your portfolio for AI-powered analysis, action recommendations,
            and loss strategies.
          </p>
        </div>

        <div className="home_page__main">
          <div className="upload_card">
            <div className="upload_card__accent" aria-hidden="true" />

            <div className="upload_card__header">
              <span className="upload_card__badge">AI-Powered Insights</span>
            </div>

            <div
              className={zone_class}
              role="button"
              tabIndex={0}
              onClick={() => file_input_ref.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") file_input_ref.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                set_drag_over(true);
              }}
              onDragLeave={() => set_drag_over(false)}
              onDrop={on_drop}
            >
              <div className="upload_zone__icon_wrap">
                <svg
                  className="upload_zone__icon"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect
                    x="8"
                    y="6"
                    width="32"
                    height="36"
                    rx="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M16 18h16M16 24h12M16 30h8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle cx="34" cy="34" r="10" fill="currentColor" opacity="0.15" />
                  <path
                    d="M34 29v10M29 34h10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="upload_zone__title">
                {selected_file ? "File ready to analyze" : "Drop your file here"}
              </p>
              <p className="upload_zone__hint">
                CSV, Excel (.xlsx, .xls) or Numbers — or click to browse
              </p>
              {selected_file && (
                <p className="upload_filename">
                  <span className="upload_filename__check">✓</span>
                  {selected_file.name}
                </p>
              )}
              <input
                ref={file_input_ref}
                type="file"
                accept=".csv,.xlsx,.xls,.xlxs,.numbers"
                style={{ display: "none" }}
                onChange={on_file_input_change}
              />
            </div>

            <button
              type="button"
              className="sample_download_link"
              onClick={download_sample_excel}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 3v12m0 0l4-4m-4 4L8 11M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Download sample Excel template
            </button>

            <div className="internal_row">
              <input
                type="checkbox"
                id="internal_chk"
                checked={internal_checked}
                onChange={(e) => on_internal_chk_change(e.target.checked)}
              />
              <label htmlFor="internal_chk">SIHO Internal Member</label>
            </div>

            {internal_checked && (
              <div className="internal_pw_wrap">
                <input
                  type="password"
                  className={pw_status.type === "err" ? "pw_error" : ""}
                  placeholder="Enter member password"
                  value={internal_password}
                  autoComplete="off"
                  onChange={(e) => on_internal_pw_input(e.target.value)}
                />
                {pw_status.text && (
                  <div className={`internal_pw_status ${pw_status.type}`}>
                    {pw_status.text}
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              className="btn_primary"
              disabled={analyzing || !selected_file}
              onClick={run_analysis}
            >
              Analyze Portfolio
            </button>

            {upload_error && <div className="upload_error">{upload_error}</div>}
        </div>

          <img
            src={hanu_home}
            alt="Portfolio analyst"
            className="home_page__hero"
          />
        </div>
      </div>

      <PortfolioPremiumLoader active={loading} complete={loader_complete} />
    </div>
  );
}
